import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Download, Info, Loader2, Timer } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Colors, { withAlpha } from '../../constants/Colors';
import { appSettingsApi, UserTeamExportResponse } from '../../services/api';

// Portal version of "script to add user team/clerk userid to team script/process_users.py".
// The backend (HomeStream-Admin-Backend routers/app_settings.py) pulls every Clerk user plus the
// user-preferences and teams tables of its own environment and returns the .xlsx base64-encoded.

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function downloadBase64(filename: string, base64: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: 'success' | 'warning' }) {
  const color = tone === 'success' ? '#15803d' : tone === 'warning' ? '#B45309' : Colors.text;
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: '12px',
        border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
        background: '#FFFFFF',
      }}
    >
      <div style={{ fontSize: '11.5px', color: Colors.textLight, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 800, color, marginTop: '2px' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

export default function UserTeamExportSettings() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<UserTeamExportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 500);
    return () => window.clearInterval(id);
  }, [running]);

  const handleGenerate = async () => {
    startedAt.current = Date.now();
    setElapsed(0);
    setError(null);
    setRunning(true);
    const response = await appSettingsApi.buildUserTeamExport();
    setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    setRunning(false);
    if (response.success && response.data) {
      setResult(response.data);
      toast.success(`Export ready: ${response.data.summary.clerk_users_fetched.toLocaleString()} users`);
    } else {
      setError(response.error || 'Failed to build user team export');
      toast.error(response.error || 'Failed to build user team export');
    }
  };

  const summary = result?.summary;

  return (
    <Card
      title="User Team Export"
      description="Every Clerk user with their favorite team, from live Clerk, user-preferences and teams data."
      headerAction={
        <Button variant="primary" className="btn-small" onClick={handleGenerate} loading={running} disabled={running}>
          {result ? 'Regenerate' : 'Generate export'}
        </Button>
      }
    >
      {/* Timer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 16px',
          borderRadius: '14px',
          marginBottom: '18px',
          background: `linear-gradient(135deg, ${withAlpha(Colors.primary, 0.1)}, ${withAlpha(Colors.secondary, 0.04)})`,
          border: `1px solid ${withAlpha(Colors.primary, 0.18)}`,
        }}
      >
        <span
          style={{
            flex: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: Colors.primary,
            color: '#FFFFFF',
          }}
        >
          {running ? <Loader2 size={20} className="spin" /> : <Timer size={20} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: Colors.primary, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
            {formatElapsed(running ? elapsed : summary ? Math.round(summary.duration_seconds) : elapsed)}
          </div>
          <div style={{ fontSize: '12.5px', color: Colors.textLight, marginTop: '3px' }}>
            {running
              ? 'Fetching all Clerk users, user preferences and teams... keep this tab open.'
              : summary
                ? `Generated ${new Date(summary.generated_at).toLocaleString()} · ${summary.environment}`
                : 'Click Generate export to build the workbook (Matched, Unmatched, Summary tabs).'}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '14px' }}>
          {error}
        </div>
      )}

      {summary && (
        <>
          {summary.clerk_total_count !== null && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12.5px',
                marginBottom: '12px',
                color: summary.clerk_counts_match ? '#15803d' : '#B45309',
              }}
            >
              {summary.clerk_counts_match ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {summary.clerk_counts_match
                ? `All ${summary.clerk_total_count.toLocaleString()} Clerk users fetched; matched + unmatched = ${(summary.matched + summary.unmatched).toLocaleString()}.`
                : `Clerk reports ${summary.clerk_total_count.toLocaleString()} users but ${summary.clerk_users_fetched.toLocaleString()} were fetched - users may have signed up during the export. Regenerate to recheck.`}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '14px' }}>
            <Stat label="Clerk users" value={summary.clerk_users_fetched} />
            <Stat label="Matched" value={summary.matched} tone="success" />
            <Stat label="Unmatched" value={summary.unmatched} tone={summary.unmatched ? 'warning' : undefined} />
            <Stat label="Preference records" value={summary.preferences_scanned} />
            <Stat label="With favorite team" value={summary.preferences_with_favorite_team} />
            <Stat label="Teams" value={summary.teams_scanned} />
          </div>

          <div style={{ fontSize: '12px', color: Colors.textLight, display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '16px' }}>
            <Info size={14} style={{ flex: 'none', marginTop: '1px' }} />
            <span>
              Unmatched: {summary.unmatched_no_preference.toLocaleString()} with no preference record,{' '}
              {summary.unmatched_no_favorite_team.toLocaleString()} with no favorite team set,{' '}
              {summary.unmatched_unknown_team_id.toLocaleString()} with a team ID not found in {summary.teams_table}.{' '}
              {summary.preferences_without_clerk_user.toLocaleString()} preference record(s) in {summary.preferences_table} have no Clerk user.
            </span>
          </div>

          <div style={{ paddingTop: '14px', borderTop: `1px solid ${withAlpha(Colors.border, 0.14)}`, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" className="btn-small" onClick={() => result && downloadBase64(result.filename, result.file_base64)} disabled={running}>
              <Download size={14} />
              Download .xlsx
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
